// 3rd Party
import Analytics from 'analytics-opencare';
import _, { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// Components
import { BlueLink, H1, P, Span } from 'common/styles/oc-styled-components';

// Other
import { colors } from 'common/styles/style-constants';
import {
  logoutUser,
  requestUserAndPatient,
  updateCheckoutErrorStatus,
  updateMatch,
  updateSelectedAppointment,
} from 'connect/actions';
import { CHECKOUT_ERROR_TYPES } from 'connect/constants';
import {
  selectDataClinic,
  selectDataLoadingUser,
  selectDataMatch,
  selectDataMatchToken,
  selectDataPatient,
  selectDataRequestedAppointment,
  selectDataUser,
} from 'connect/selectors';
import { getParam } from 'pages/common/helpers/url-helpers';
import {
  DIMENSIONS,
  useWindowDimensions,
} from 'providers/WindowDimensionsProvider';
import { useAnalyticsService } from 'services/AnalyticsService';
import { getSpecialistAnalyticsData } from 'utils/analytics';
import { usePrevious } from 'utils/customHooks/usePrevious';

import Error from '../../../../common/components/Error/Error';
import { useFunnelStorage } from '../../../../funnel/storage/Storage';
import { StepLoading, StyledLock } from '../../../checkout-styled-components';
import { selectDataCheckoutFormErrorStatus } from '../../../connect/selectors';
import {
  SSO_APPOINTMENT,
  SSO_PROVIDERS,
  SSO_REDIRECT,
} from '../../../helpers/checkout-constants';
import PatientForm from '../PatientForm/PatientForm';

const analytics = new Analytics();

// Component
const PatientStep = ({ handleNextStep, state }) => {
  const dispatch = useDispatch();
  const funnelStorage = useFunnelStorage();
  const { search } = useLocation();
  const { layout } = useWindowDimensions();

  const analyticsService = useAnalyticsService();

  const checkoutFormErrorStatus = useSelector(
    selectDataCheckoutFormErrorStatus,
  );
  const patient = useSelector(selectDataPatient);
  const loadingUser = useSelector(selectDataLoadingUser);
  const user = useSelector(selectDataUser);
  const previousUser = usePrevious(user);
  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const clinic = useSelector(selectDataClinic);
  const match = useSelector(selectDataMatch);
  const matchToken = useSelector(selectDataMatchToken);

  const userIsAuthenticated = get(user, 'authenticated');
  const userIsPatient = (get(user, 'editable.patients') || []).length > 0;

  const [isLoading, setIsLoading] = useState(true);
  const [hasAccount, setHasAccount] = useState(false);

  useEffect(() => {
    // Double check with API whether user is logged in or not when both are true:
    // 1. User is not logged in at start of checkout
    // 2. User is not coming back from SSO
    if (!search && !get(user, 'authenticated')) {
      dispatch(requestUserAndPatient());
    }
  }, []);

  // All necessary data is loaded
  useEffect(() => {
    if (
      !loadingUser
      && user
      && requestedAppointment
      && (!search || !getParam(search, SSO_REDIRECT)[0]) // ignore this if ssoRedirect param exists
    ) {
      setIsLoading(false);
    }
  }, [user, requestedAppointment, search, loadingUser]);

  // Initial analytics page view event
  useEffect(() => {
    if (requestedAppointment.providerId && !checkoutFormErrorStatus) {
      // No user is logged in
      let section = 'login';

      // Patient is logged in with SSO
      if (
        patient
        && patient.id
        && [
          SSO_PROVIDERS.GOOGLE,
          SSO_PROVIDERS.FACEBOOK,
          SSO_PROVIDERS.APPLE,
        ].includes(user.authStrategy)
      ) {
        section = 'sso-confirm-login';
      }

      // Patient is logged in manually
      if (
        patient
        && patient.id
        && ![
          SSO_PROVIDERS.GOOGLE,
          SSO_PROVIDERS.FACEBOOK,
          SSO_PROVIDERS.APPLE,
        ].includes(user.authStrategy)
      ) {
        section = 'confirm-login';
      }

      analytics.trackPageView({
        pageType: analytics.pageTypes.FORM,
        pageName: 'form-booking-account',
        name: 'form-booking',
        section,
        practiceId: requestedAppointment.provider?.clinic,
        providerId: requestedAppointment.providerId,
        customProperties: getSpecialistAnalyticsData(match),
        ...analyticsService.analyticsData,
      });
    }
  }, [patient, requestedAppointment, checkoutFormErrorStatus]);

  // Analytics page view events for error changes
  useEffect(() => {
    if (checkoutFormErrorStatus) {
      // Capture errors in content object
      let content = null;

      if (
        checkoutFormErrorStatus
        === CHECKOUT_ERROR_TYPES.LOGGED_IN_AS_NOT_PATIENT
      ) {
        content = { 'error-already-practice-login': 'true' };
      }

      if (
        checkoutFormErrorStatus
        === CHECKOUT_ERROR_TYPES.EMAIL_ALREADY_REGISTERED
      ) {
        content = { 'error-already-registered': 'true' };
      }

      if (checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.SSO_DENIED_ACCESS) {
        content = { 'error-sso-permission': 'true' };
      }

      if (checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.SSO_GENERIC) {
        content = { 'error-sso-issue': 'true' };
      }

      analytics.trackPageView({
        pageType: analytics.pageTypes.FORM_ERROR,
        pageName: 'form-booking-account',
        name: 'form-booking',
        section: 'login',
        content,
        practiceId: requestedAppointment.provider?.clinic,
        providerId: requestedAppointment.providerId,
        ...analyticsService.analyticsData,
      });
    }
  }, [checkoutFormErrorStatus]);

  useEffect(() => {
    // User is logged in and not a patient, trigger error
    if (userIsAuthenticated && !userIsPatient) {
      dispatch(
        updateCheckoutErrorStatus(
          CHECKOUT_ERROR_TYPES.LOGGED_IN_AS_NOT_PATIENT,
        ),
      );
    }
  }, [user]);

  useEffect(() => {
    // Clear error if:
    // 1. user is patient & was seeing "please sign in" error
    // 2. if user has logged out and any error remains
    if (
      (patient
      && checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.EMAIL_ALREADY_REGISTERED)
      || (checkoutFormErrorStatus !== null
      && previousUser
      && user.authenticated !== previousUser.authenticated
      && !user.authenticated)
    ) {
      dispatch(updateCheckoutErrorStatus(null));
    }
  }, [patient, user]);

  // Listen to SSO errors
  useEffect(() => {
    const supportedErrors = {
      ACCESS_DENIED: CHECKOUT_ERROR_TYPES.SSO_DENIED_ACCESS,
      GENERIC: CHECKOUT_ERROR_TYPES.SSO_GENERIC,
    };

    const ssoError = getParam(search, 'error')[0];

    if (ssoError) {
      dispatch(
        updateCheckoutErrorStatus(
          supportedErrors[ssoError] || supportedErrors.GENERIC,
        ),
      );

      setIsLoading(false);
    }
  }, [search]);

  // Listen to SSO redirect to resave requested appointment & push user to next step
  useEffect(() => {
    if (search && clinic.name) {
      const ssoRedirect = getParam(search, SSO_REDIRECT)[0];
      const savedProvider = getParam(search, SSO_APPOINTMENT.PROVIDER)[0];
      const savedSlot = getParam(search, SSO_APPOINTMENT.SLOT)[0];
      const anyProviderSelected = getParam(
        search,
        SSO_APPOINTMENT.ANY_PROVIDER,
      )[0];
      const provider = clinic.providers.find(
        (pr) => pr.id.toString() === savedProvider,
      );

      if (ssoRedirect && savedProvider && provider) {
        dispatch(
          updateSelectedAppointment(
            parseInt(savedProvider, 10),
            parseInt(savedSlot, 10),
            provider,
            !anyProviderSelected ? null : anyProviderSelected === 'true',
          ),
        );

        if (get(user, 'authenticated') && patient) {
          // Add ownership of match to authenticated user
          dispatch(updateMatch(match.id, matchToken));

          handleNextStep();
        }

        setIsLoading(false);
        analytics.trackSignUp({
          pageType: analytics.pageTypes.FORM,
          pageName: 'form-booking-account',
          name: 'form-booking',
          section: 'account',
          content: {
            ssoProviderName: _.get(user, 'authStrategy'),
          },
          practiceId: clinic.id,
          providerId: savedProvider,
          userId: _.get(user, 'id'),
          patientId: _.get(patient, 'id'),
          funnelConfigId: _.get(funnelStorage, 'config.id'),
        });
      }
    }
  }, [search, clinic, user, patient]);

  const handleLogout = () => {
    analytics.clearUserId();
    dispatch(logoutUser());
    dispatch(updateCheckoutErrorStatus(null));
  };

  const renderErrorMessage = () => {
    if (
      !checkoutFormErrorStatus
      || hasAccount
      || checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.PHONE_INVALID
    ) {
      return null;
    }

    return (
      <Error dataTestId="patientStepError" style={{ marginBottom: '30px' }}>
        <P color={colors.danger} data-testid={checkoutFormErrorStatus}>
          {checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.GENERIC
          && <>Something went wrong while submitting your details.</>}
          {checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.EMAIL_INVALID
          && <>This email address is invalid, please check for typos.</>}
          {checkoutFormErrorStatus
          === CHECKOUT_ERROR_TYPES.EMAIL_ALREADY_REGISTERED
          && (
            <>
              This email has already been registered,{' '}
              <Span display="block">
                please
                <BlueLink onClick={() => setHasAccount(true)}>
                  {' '}
                  sign in
                </BlueLink>{' '}
                then refresh this page.
              </Span>
            </>
          )}
          {checkoutFormErrorStatus
          === CHECKOUT_ERROR_TYPES.LOGGED_IN_AS_NOT_PATIENT
          && (
            <>
              You're currently logged in and not a patient.{' '}
              <Span display="block">
                Please
                <BlueLink onClick={handleLogout}> log out </BlueLink>
                to complete this action.
              </Span>
            </>
          )}
          {checkoutFormErrorStatus
          === CHECKOUT_ERROR_TYPES.SSO_DENIED_ACCESS
          && (
            <>
              We're unable to create your account without access to your Google
              contact info.
            </>
          )}
          {checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.SSO_GENERIC
          && <>There was an issue creating your account. Please try again.</>}
        </P>
      </Error>
    );
  };

  const renderHeaderCopy = () => {
    if (patient) {
      return (
        <H1
          data-testid="patientStepHeading"
          fontSize="20px"
          margin="-12px 0 0 auto"
        >
          Confirm This Is You
          <StyledLock
            aria-hidden
            bottom="5px"
            margin="0 0 0 8px"
            size="16px"
            theme="filled"
            type="lock"
            role="presentation"
          />
        </H1>
      );
    }

    return (
      <H1
        data-testid="patientStepHeading"
        fontSize="24px"
        margin={layout === DIMENSIONS.MOBILE ? '-25px 0 5px auto' : '-15px 0 5px auto'}
        lineHeight="normal"
        color={colors.navy}
      >
        {hasAccount
          ? 'Log in to your account to book your appointment'
          : 'Enter your contact details to book your appointment'}
      </H1>
    );
  };

  if (isLoading) {
    return (
      <div data-testid="loading">
        <StepLoading type="loading" />
      </div>
    );
  }

  return (
    <div>
      {renderErrorMessage()}
      {renderHeaderCopy()}
      <P color={colors.slate} margin="0 0 16px">
        We will keep your information safe &#38; secure.
        <StyledLock
          aria-hidden
          bottom="5px"
          margin="0 0 0 5px"
          size="16px"
          theme="filled"
          type="lock"
          role="presentation"
        />
      </P>
      <PatientForm
        handleNextStep={handleNextStep}
        state={state}
        hasAccount={hasAccount}
        setHasAccount={setHasAccount}
      />
    </div>
  );
};

PatientStep.propTypes = {
  handleNextStep: PropTypes.func,
  state: PropTypes.object,
};

export default PatientStep;
